import type { TextChain } from '@/api/types/stowed-element'
import { chainContaining } from './helpers/chain-queries'

export const TEXT_LINK_LAYER = 'userlayer'

/** The next unused numeric chain id: one past the highest numeric id in `chains`. */
export function nextNumericId(chains: readonly TextChain[]): string {
  let max = 0

  for (const chain of chains) {
    const n = Number(chain.id)

    if (Number.isFinite(n) && n > max) max = n
  }

  return String(max + 1)
}

/**
 * Links `sourceUid` -> `targetUid`. Merges existing chains when either frame already belongs
 * to one; otherwise starts a new chain. Caller must validate with `canLink` first.
 */
export function addLink(
  chains: TextChain[],
  sourceUid: string,
  targetUid: string,
  layer: string = TEXT_LINK_LAYER
): TextChain[] {
  const sourceChain = chainContaining(chains, sourceUid)
  const targetChain = chainContaining(chains, targetUid)

  const sourceMembers = sourceChain ? sourceChain.textBox : [{ uid: sourceUid }]
  const targetMembers = targetChain ? targetChain.textBox : [{ uid: targetUid }]

  const merged = [...sourceMembers, ...targetMembers]
  const keepId = sourceChain?.id ?? targetChain?.id ?? nextNumericId(chains)
  const remaining = chains.filter(
    (chain) => chain.id !== sourceChain?.id && chain.id !== targetChain?.id
  )

  return [...remaining, { id: keepId, layer, textBox: merged }]
}

/** Splits the chain containing `uid` immediately after it, unlinking the outgoing connection. */
export function removeLinkAfter(chains: TextChain[], uid: string): TextChain[] {
  const chain = chainContaining(chains, uid)

  if (!chain) return chains
  const idx = chain.textBox.findIndex((tb) => tb.uid === uid)

  if (idx < 0 || idx >= chain.textBox.length - 1) return chains

  const head = chain.textBox.slice(0, idx + 1)
  const tail = chain.textBox.slice(idx + 1)
  const remaining = chains.filter((c) => c.id !== chain.id)
  const rebuilt: TextChain[] = []

  if (head.length >= 2) rebuilt.push({ id: chain.id, layer: chain.layer, textBox: head })
  if (tail.length >= 2)
    rebuilt.push({ id: nextNumericId([...chains, ...rebuilt]), layer: chain.layer, textBox: tail })

  return [...remaining, ...rebuilt]
}

/** Removes `uid` from any chain it belongs to, splicing its neighbors together (delete repair). */
export function removeBoxFromChains(chains: TextChain[], uid: string): TextChain[] {
  const chain = chainContaining(chains, uid)

  if (!chain) return chains

  const spliced = chain.textBox.filter((tb) => tb.uid !== uid)
  const remaining = chains.filter((c) => c.id !== chain.id)

  if (spliced.length < 2) return remaining
  return [...remaining, { id: chain.id, layer: chain.layer, textBox: spliced }]
}

/**
 * Highest uid named by any chain, whether or not an element still carries it.
 *
 * Seeds the uid allocator alongside `readMaxUidFromCheckpointMetadata` (WMPCM-7036). That reader
 * scans loaded *elements* only, so a uid freed by a deleted frame is immediately reissuable — and if
 * a stale chain still names it, the newly created frame silently inherits that chain membership and
 * appears linked to any client that loads the spread. Reading the RAW, pre-sanitize list is the
 * point: the uids `sanitizeChains` is about to drop are exactly the ones that must not be recycled.
 */
export function maxUidInChains(chains: readonly TextChain[]): number {
  let max = 0

  for (const chain of chains) {
    for (const tb of chain.textBox) {
      const n = Number(tb.uid)

      if (Number.isFinite(n) && n > max) max = n
    }
  }

  return max
}

/**
 * Repairs chains corrupted by (a) a duplicate uid within a single chain (e.g. a pre-fix
 * copy/paste that cloned a linked frame's uid — WMPCM-6852) and, when `validUids` is supplied,
 * (b) references to uids that no longer correspond to any loaded text box (e.g. a remote delete
 * this client missed — WMPCM-6868). Drops repeated occurrences of a uid within a chain (keeping
 * the first) and any uid absent from `validUids`, then drops any chain left with fewer than 2
 * members.
 */
export function sanitizeChains(chains: TextChain[], validUids?: Set<string>): TextChain[] {
  const sanitized: TextChain[] = []

  for (const chain of chains) {
    const seen = new Set<string>()
    const deduped = chain.textBox.filter((tb) => {
      if (seen.has(tb.uid)) return false
      if (validUids && !validUids.has(tb.uid)) return false
      seen.add(tb.uid)
      return true
    })

    if (deduped.length >= 2) sanitized.push({ ...chain, textBox: deduped })
  }

  return sanitized
}
